import { useState, useEffect, useCallback } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  HStack,
  SimpleGrid,
  Select,
  Spinner,
  Icon,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faLightbulb,
  faTriangleExclamation,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { insightsApi } from '../api';
import { insightsCountAtom } from '../state/insightsAtom';
import { InsightCard } from '../components/insights/InsightCard';

const STATUS_OPTIONS = [
  { value: 'active', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];
const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'ad_ops', label: 'Ad Ops' },
  { value: 'web', label: 'Web' },
];
const SEVERITY_OPTIONS = [
  { value: 'all', label: 'All priorities' },
  { value: 'critical', label: 'Action needed' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'info', label: 'For info' },
];

function SummaryChip({ icon, color, label, value }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  return (
    <Flex bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" p={4} align="center" gap={3}>
      <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg={`${color}.50`} _dark={{ bg: `${color}.900` }}>
        <Icon color={`${color}.500`} boxSize={4}><FontAwesomeIcon icon={icon} /></Icon>
      </Flex>
      <Box>
        <Text fontSize="2xl" fontWeight="700" lineHeight="1">{value}</Text>
        <Text fontSize="xs" color={textSecondary}>{label}</Text>
      </Box>
    </Flex>
  );
}

function InsightsPage() {
  const [insights, setInsights] = useState([]);
  const [counts, setCounts] = useState({ active: 0, critical: 0 });
  const [lastGenerated, setLastGenerated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  const selectedSite = useRecoilValue(selectedSiteAtom);
  const setBadgeCount = useSetRecoilState(insightsCountAtom);
  const toast = useToast();

  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const emptyBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const applyResponse = useCallback((res) => {
    setInsights(res.data || []);
    setCounts(res.counts || { active: 0, critical: 0 });
    setLastGenerated(res.lastGenerated || null);
    if (res.counts) setBadgeCount(res.counts);
  }, [setBadgeCount]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await insightsApi.list({
        status: statusFilter,
        category: categoryFilter,
        severity: severityFilter,
        site: selectedSite,
      });
      applyResponse(res);
    } catch (error) {
      toast({ title: 'Failed to load insights', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, severityFilter, selectedSite, applyResponse, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await insightsApi.generate({ site: selectedSite });
      await fetchData();
      toast({ title: 'Insights refreshed', status: 'success', duration: 2500 });
    } catch (error) {
      toast({ title: 'Refresh failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setRefreshing(false);
    }
  };

  const updateStatus = async (id, action) => {
    setBusyId(id);
    try {
      await (action === 'acknowledge' ? insightsApi.acknowledge(id) : insightsApi.dismiss(id));
      // Refresh counts for the badge and reconcile the current filtered view.
      const res = await insightsApi.count({ site: selectedSite });
      setCounts(res);
      setBadgeCount(res);
      // Remove from view if it no longer matches the active-status filter.
      setInsights((prev) =>
        prev.filter((i) => {
          if (i.insight_id !== id) return true;
          if (statusFilter === 'all') return true;
          if (action === 'acknowledge') return statusFilter === 'acknowledged';
          return statusFilter === 'dismissed';
        }).map((i) =>
          i.insight_id === id && statusFilter === 'all'
            ? { ...i, status: action === 'acknowledge' ? 'acknowledged' : 'dismissed' }
            : i
        )
      );
    } catch (error) {
      toast({ title: `Failed to ${action}`, description: error.message, status: 'error', duration: 5000 });
    } finally {
      setBusyId(null);
    }
  };

  const lastText = lastGenerated
    ? `Updated ${new Date(lastGenerated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
    : 'Not yet generated';

  return (
    <Box>
      {/* Header */}
      <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={{ base: 6, md: 8 }} gap={4}>
        <Box>
          <Heading size={{ base: 'lg', md: 'xl' }} bgGradient="linear(to-r, brand.500, accent.500)" bgClip="text" fontWeight="700">
            Insights
          </Heading>
          <Text color={textSecondary} mt={1}>What to act on next, from your traffic and ad performance</Text>
        </Box>
        <HStack spacing={3}>
          <Text fontSize="xs" color={textSecondary} display={{ base: 'none', md: 'block' }}>{lastText}</Text>
          <Button
            leftIcon={<FontAwesomeIcon icon={faRotate} />}
            size="sm"
            colorScheme="brand"
            variant="outline"
            borderRadius="xl"
            isLoading={refreshing}
            loadingText="Refreshing"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Summary */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
        <SummaryChip icon={faTriangleExclamation} color="red" label="Need action" value={counts.critical} />
        <SummaryChip icon={faLightbulb} color="brand" label="New insights" value={counts.active} />
        <SummaryChip icon={faCircleCheck} color="green" label="Showing now" value={insights.length} />
      </SimpleGrid>

      {/* Filters */}
      <HStack spacing={3} mb={6} wrap="wrap">
        <Select size="sm" w="180px" borderRadius="xl" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select size="sm" w="180px" borderRadius="xl" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select size="sm" w="180px" borderRadius="xl" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
          {SEVERITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </HStack>

      {/* List */}
      {loading ? (
        <Flex justify="center" py={16}><Spinner size="xl" color="brand.500" thickness="4px" /></Flex>
      ) : insights.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={16} bg={emptyBg} borderRadius="2xl" gap={3}>
          <Icon color="green.400" boxSize={10}><FontAwesomeIcon icon={faCircleCheck} /></Icon>
          <Text fontWeight="600" fontSize="lg">You're all caught up</Text>
          <Text color={textSecondary} fontSize="sm" textAlign="center" maxW="md">
            No {statusFilter === 'active' ? 'new ' : ''}insights match these filters. New recommendations appear here as your data changes.
          </Text>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} alignItems="stretch">
          {insights.map((insight) => (
            <InsightCard
              key={insight.insight_id}
              insight={insight}
              busy={busyId === insight.insight_id}
              onAcknowledge={(id) => updateStatus(id, 'acknowledge')}
              onDismiss={(id) => updateStatus(id, 'dismiss')}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}

export default InsightsPage;
